import React, { useState } from 'react';
import { 
  Users, UserPlus, FileSpreadsheet, Download, Upload, 
  CheckCircle, Search, Filter, Trash2, Eye, Shield, FileText, Lock, AlertTriangle, CheckCircle2,
  Grid, Sparkles, ShieldCheck, Activity, CreditCard, TrendingUp, BarChart2, Key, Copy, Check, 
  GraduationCap, Building2, Stethoscope, ChevronRight, Brain, ArrowUpRight
} from 'lucide-react';
import { ModuleExplorer } from './ModuleExplorer';
import { UserSession } from '../AppShell';
import { DEMO_STUDENTS, DemoStudent, getAccessibleStudents } from '../data/demoStudents';
import { StudentClinicalAnalysisModal } from './StudentClinicalAnalysisModal';
import { ModuleTutorialHub } from './ModuleTutorialHub';
import { DISABILITY_CLUSTERS, getDisabilityForStudent, DisabilityCategory } from '../data/disabilityTaxonomy';
import { ClinicalCockpitPanel } from './ClinicalCockpitPanel';

interface TenantAdminDashboardProps {
  isSubscriptionActive?: boolean;
  currentUser?: UserSession | null;
  initialDepartmentId?: string | null;
  onNavigateToDisabilitySuite?: (clusterId: string) => void;
}

export const TenantAdminDashboard: React.FC<TenantAdminDashboardProps> = ({ 
  isSubscriptionActive = true, 
  currentUser,
  initialDepartmentId,
  onNavigateToDisabilitySuite
}) => {
  const [students, setStudents] = useState<DemoStudent[]>(getAccessibleStudents(currentUser));
  const [dashboardTab, setDashboardTab] = useState<'cockpit' | 'wings' | 'modules' | 'tutorials' | 'roster' | 'add_single' | 'bulk_upload' | 'subscription'>('cockpit');
  const [selectedWingFilter, setSelectedWingFilter] = useState<string>(initialDepartmentId || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudentForModal, setSelectedStudentForModal] = useState<DemoStudent | null>(null);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  React.useEffect(() => {
    setStudents(getAccessibleStudents(currentUser));
  }, [currentUser]);

  React.useEffect(() => {
    if (initialDepartmentId) {
      setSelectedWingFilter(initialDepartmentId);
      setDashboardTab('roster');
    }
  }, [initialDepartmentId]);

  // Single Add State with Student Login Credentials
  const [singleName, setSingleName] = useState('');
  const [singleRollId, setSingleRollId] = useState('');
  const [singleGrade, setSingleGrade] = useState('Class 3-A');
  const [singleCategory, setSingleCategory] = useState('Autism Spectrum (ASD)');
  const [singleGuardianName, setSingleGuardianName] = useState('');
  const [singleGuardianPhone, setSingleGuardianPhone] = useState('');
  const [singleTherapist, setSingleTherapist] = useState('Sarah Jenkins (SLP)');
  const [singleStudentEmail, setSingleStudentEmail] = useState('');
  const [singleStudentPassword, setSingleStudentPassword] = useState('');
  const [singlePortalPin, setSinglePortalPin] = useState('1234');

  // Bulk Upload State
  const [file, setFile] = useState<File | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [parsedPreview, setParsedPreview] = useState<any[]>([]);

  if (!isSubscriptionActive) {
    return (
      <div className="max-w-4xl mx-auto py-16 px-4">
        <div className="bg-white rounded-3xl border border-red-200 shadow-2xl p-8 text-center space-y-6">
          <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
            <Lock className="w-8 h-8" />
          </div>

          <div className="space-y-2">
            <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-bold rounded-full uppercase tracking-wider border border-red-300">
              Active Subscription Required
            </span>
            <h2 className="text-3xl font-black text-slate-900">Tenant Subscription Inactive</h2>
          </div>
        </div>
      </div>
    );
  }

  const handleOpenAnalysis = (st: DemoStudent) => {
    setSelectedStudentForModal(st);
    setIsAnalysisModalOpen(true);
  };

  const handleCopyCredentials = (st: DemoStudent) => {
    const text = `Student Login Credentials for ${st.name}:\nEmail: ${st.studentEmail}\nPassword: ${st.studentPassword || 'Student#2026'}\nPortal PIN: ${st.portalPin || '1234'}\nPortal URL: https://abilityos.bugfreesolution.com`;
    navigator.clipboard.writeText(text);
    setCopiedId(st.id);
    setTimeout(() => setCopiedId(null), 2500);
  };

  const handleAddSingleStudent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!singleName || !singleGuardianName) return;

    const email = singleStudentEmail || `${singleName.toLowerCase().replace(/\s+/g, '.')}@bugfreeschool.org`;
    const pwd = singleStudentPassword || `${singleName.split(' ')[0]}#2026Password`;

    const newStudent: DemoStudent = {
      id: Date.now().toString(),
      name: singleName,
      rollId: singleRollId || `STU-2026-00${students.length + 1}`,
      age: 8,
      grade: singleGrade,
      disabilityCategory: singleCategory,
      guardianName: singleGuardianName,
      guardianPhone: singleGuardianPhone || '+91 98000 00000',
      therapist: singleTherapist,
      studentEmail: email,
      studentPassword: pwd,
      portalPin: singlePortalPin || '1234',
      tenant: currentUser?.tenant || 'Sunshine Autism Academy',
      iepStatus: 'ACTIVE',
      iepGoal: 'New SMART IEP Goal assigned upon enrolment',
      recentScore: 'Initial Assessment Pending',
      overallMasteryPercent: 75,
      clinicalSummary: 'Newly enrolled student profile with inclusive login credentials.',
      aiRecommendation: 'Conduct initial diagnostic assessment in DiagnosticAssessmentModule.',
      moduleMasteryMap: {}
    };

    setStudents([newStudent, ...students]);
    setSingleName('');
    setSingleRollId('');
    setSingleGuardianName('');
    setSingleGuardianPhone('');
    setSingleStudentEmail('');
    setSingleStudentPassword('');
    setDashboardTab('roster');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      setFile(selected);

      const mockParsed = [
        { name: 'Miss Diya Sengupta', rollId: 'STU-2026-101', grade: 'Class 1-A', category: 'Hearing Impairment', guardian: 'Rohan Sengupta', phone: '+91 98450 11990', therapist: 'Sarah Jenkins (SLP)', email: 'diya.sengupta@bugfreeschool.org', pwd: 'Diya#2026Password', pin: '4567' },
        { name: 'Master Ishan Verma', rollId: 'STU-2026-102', grade: 'Class 3-B', category: 'Down Syndrome', guardian: 'Meera Verma', phone: '+91 98765 43210', therapist: 'Michael Chen (OT)', email: 'ishan.verma@bugfreeschool.org', pwd: 'Ishan#2026Password', pin: '5678' },
        { name: 'Master Tanmay Roy', rollId: 'STU-2026-103', grade: 'Class 6-A', category: 'Visual Impairment (Braille)', guardian: 'Kunal Roy', phone: '+91 98112 23344', therapist: 'Priya Nair (PT)', email: 'tanmay.roy@bugfreeschool.org', pwd: 'Tanmay#2026Password', pin: '6789' },
      ];
      setParsedPreview(mockParsed);
    }
  };

  const handleConfirmBulkUpload = () => {
    if (parsedPreview.length === 0) return;

    const uploadedStudents: DemoStudent[] = parsedPreview.map((item, idx) => ({
      id: (Date.now() + idx).toString(),
      name: item.name,
      rollId: item.rollId,
      age: 9,
      grade: item.grade,
      disabilityCategory: item.category,
      guardianName: item.guardian,
      guardianPhone: item.phone,
      therapist: item.therapist,
      studentEmail: item.email,
      studentPassword: item.pwd,
      portalPin: item.pin,
      tenant: currentUser?.tenant || 'Sunshine Autism Academy',
      iepStatus: 'ACTIVE',
      iepGoal: 'Bulk imported IEP goal milestone',
      recentScore: '85% Initial Score',
      overallMasteryPercent: 80,
      clinicalSummary: 'Bulk imported student roster record with student login credentials.',
      aiRecommendation: 'Initialize baseline therapy goals.',
      moduleMasteryMap: {}
    }));

    setStudents([...uploadedStudents, ...students]);
    setUploadSuccess(`Successfully imported ${parsedPreview.length} students with inclusive login credentials from "${file?.name}"!`);
    setFile(null);
    setParsedPreview([]);
    setDashboardTab('roster');

    setTimeout(() => setUploadSuccess(''), 4000);
  };

  const filteredStudents = students.filter((s) => {
    const matchesSearch = 
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.rollId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.disabilityCategory.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.studentEmail.toLowerCase().includes(searchTerm.toLowerCase());

    if (selectedWingFilter === 'all') return matchesSearch;
    const cluster = getDisabilityForStudent(s.disabilityCategory);
    return matchesSearch && cluster.id === selectedWingFilter;
  });

  return (
    <div className="space-y-6 max-w-7xl mx-auto pt-28 pb-16 px-4">
      
      {/* 0. PAGE-SPECIFIC WORKSPACE RIBBON */}
      <div className="bg-[#1c1b1f] text-white p-4 sm:p-5 rounded-[28px] border border-white/10 shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#ffd64e] text-black flex items-center justify-center font-black shadow-md shrink-0">
            <Building2 className="w-5 h-5 text-black" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#ffd64e] animate-pulse" />
              <span className="text-white/60 uppercase font-black tracking-wider text-[10px]">
                Active Page-Specific Workspace
              </span>
            </div>
            <div className="text-base sm:text-lg font-black text-white flex flex-wrap items-center gap-2 mt-0.5">
              <span>{currentUser?.tenant || 'AbilityOS Special Academy'}</span>
              <span className="text-xs font-semibold text-[#ffd64e] bg-white/10 px-2.5 py-0.5 rounded-full border border-white/10">
                Institutional Admin & 8 Clinical Wings
              </span>
            </div>
          </div>
        </div>

        {/* Pivot to Clinical Disability Suites */}
        <div className="flex flex-wrap items-center gap-2 self-start md:self-auto">
          {onNavigateToDisabilitySuite && (
            <button
              onClick={() => onNavigateToDisabilitySuite('autism-neuro')}
              className="px-4 py-2 rounded-full bg-white/10 hover:bg-[#ffd64e] hover:text-black text-white font-bold text-xs transition-all flex items-center gap-1.5 border border-white/15 cursor-pointer shadow-xs group"
              title="Open Clinical Disability Suites"
            >
              <Stethoscope className="w-3.5 h-3.5 text-[#ffd64e] group-hover:text-black" />
              <span>Clinical Disability Suites</span>
              <ChevronRight className="w-3 h-3 opacity-60" />
            </button>
          )}
        </div>
      </div>

      {/* Clarvos Obsidian Bento Header Banner */}
      <div className="bg-[#1c1b1f] text-white p-7 sm:p-8 rounded-[32px] shadow-2xl border border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden backdrop-blur-xl">
        <div className="space-y-3 text-left z-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-[#ffd64e]/10 text-[#ffd64e] text-xs font-black rounded-full border border-[#ffd64e]/30 uppercase tracking-wider">
            <ShieldCheck className="w-4 h-4 text-[#ffd64e]" />
            <span>Admin Control Plane • {currentUser?.tenant || 'BugFree Solutions Demo Organization'}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-white">
            Welcome, {currentUser?.name || 'Demo Admin User'}
          </h1>
          <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
            Logged in as <strong className="text-white font-mono">{currentUser?.email || 'demo@bugfreesolution.com'}</strong>. Structured across <span className="text-[#ffd64e] font-black">8 Clinical Disability Wings under RPwD Act 2016 & DSM-5</span>.
          </p>
        </div>

        <div className="flex items-center gap-3 bg-white/5 p-3 rounded-[24px] border border-white/10 backdrop-blur-md shrink-0 z-10">
          <div className="text-center px-4 border-r border-white/10">
            <div className="text-2xl font-black text-[#ffd64e] font-mono">8 / 8</div>
            <div className="text-[10px] uppercase font-bold tracking-wider text-white/60">Clinical Wings</div>
          </div>
          <div className="text-center px-4">
            <div className="text-2xl font-black text-[#bfecc7] font-mono">{students.length}</div>
            <div className="text-[10px] uppercase font-bold tracking-wider text-white/60">Demo Students</div>
          </div>
        </div>
      </div>

      {/* Clarvos Tab Navigation Bar */}
      <div className="bg-[#1c1b1f] p-2 rounded-[28px] border border-white/10 shadow-lg flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setDashboardTab('cockpit')}
            className={`px-5 py-2.5 rounded-full font-black text-xs transition-all flex items-center gap-2 cursor-pointer ${
              dashboardTab === 'cockpit' 
                ? 'bg-[#ffd64e] text-[#1c1b1f] shadow-md scale-[1.02]' 
                : 'bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-white/5'
            }`}
          >
            <Stethoscope className="w-4 h-4 text-current" />
            <span>🩺 Live Clinical Cockpit</span>
          </button>

          <button
            onClick={() => setDashboardTab('wings')}
            className={`px-5 py-2.5 rounded-full font-black text-xs transition-all flex items-center gap-2 cursor-pointer ${
              dashboardTab === 'wings' 
                ? 'bg-[#ffd64e] text-[#1c1b1f] shadow-md scale-[1.02]' 
                : 'bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-white/5'
            }`}
          >
            <Building2 className="w-4 h-4 text-current" />
            <span>🏥 8 Clinical Disability Wings</span>
          </button>

          <button
            onClick={() => setDashboardTab('roster')}
            className={`px-5 py-2.5 rounded-full font-black text-xs transition-all flex items-center gap-2 cursor-pointer ${
              dashboardTab === 'roster' 
                ? 'bg-[#ffd64e] text-[#1c1b1f] shadow-md scale-[1.02]' 
                : 'bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-white/5'
            }`}
          >
            <Users className="w-4 h-4 text-current" />
            <span>Student Roster & Credentials ({students.length})</span>
          </button>

          <button
            onClick={() => setDashboardTab('modules')}
            className={`px-5 py-2.5 rounded-full font-black text-xs transition-all flex items-center gap-2 cursor-pointer ${
              dashboardTab === 'modules' 
                ? 'bg-[#ffd64e] text-[#1c1b1f] shadow-md scale-[1.02]' 
                : 'bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-white/5'
            }`}
          >
            <Grid className="w-4 h-4 text-current" />
            <span>🚀 30-Module Suite</span>
          </button>

          <button
            onClick={() => setDashboardTab('tutorials')}
            className={`px-5 py-2.5 rounded-full font-black text-xs transition-all flex items-center gap-2 cursor-pointer ${
              dashboardTab === 'tutorials' 
                ? 'bg-[#ffd64e] text-[#1c1b1f] shadow-md scale-[1.02]' 
                : 'bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-white/5'
            }`}
          >
            <GraduationCap className="w-4 h-4 text-current" />
            <span>🎓 Clinical Tutorials</span>
          </button>

          <button
            onClick={() => setDashboardTab('add_single')}
            className={`px-5 py-2.5 rounded-full font-black text-xs transition-all flex items-center gap-2 cursor-pointer ${
              dashboardTab === 'add_single' 
                ? 'bg-[#ffd64e] text-[#1c1b1f] shadow-md scale-[1.02]' 
                : 'bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-white/5'
            }`}
          >
            <UserPlus className="w-4 h-4 text-current" />
            <span>Enrol Single Student</span>
          </button>

          <button
            onClick={() => setDashboardTab('bulk_upload')}
            className={`px-5 py-2.5 rounded-full font-black text-xs transition-all flex items-center gap-2 cursor-pointer ${
              dashboardTab === 'bulk_upload' 
                ? 'bg-[#ffd64e] text-[#1c1b1f] shadow-md scale-[1.02]' 
                : 'bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-white/5'
            }`}
          >
            <FileSpreadsheet className="w-4 h-4 text-current" />
            <span>Bulk Upload Excel</span>
          </button>
        </div>
      </div>

      {uploadSuccess && (
        <div className="p-4 bg-[#bfecc7]/20 border border-[#bfecc7]/50 text-[#1c1b1f] rounded-[20px] text-xs font-bold flex items-center gap-2 shadow-sm">
          <CheckCircle className="w-5 h-5 text-emerald-700" />
          {uploadSuccess}
        </div>
      )}

      {/* VIEW -1: LIVE CLINICAL 3-PANE COCKPIT */}
      {dashboardTab === 'cockpit' && (
        <ClinicalCockpitPanel currentUser={currentUser} />
      )}

      {/* VIEW 0: 8 CLINICAL DISABILITY WINGS */}
      {dashboardTab === 'wings' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full bg-[#ffd64e]/20 text-slate-900 border border-[#ffd64e]/40">
                Institutional Clinical Architecture
              </span>
              <h2 className="text-2xl font-black text-[#1c1b1f] mt-1">
                8 Clinical Disability Departments
              </h2>
              <p className="text-xs text-slate-600">
                Organized under the Rights of Persons with Disabilities (RPwD) Act 2016 and DSM-5-TR clinical diagnostic criteria.
              </p>
            </div>

            <button
              onClick={() => {
                setSelectedWingFilter('all');
                setDashboardTab('roster');
              }}
              className="px-5 py-2.5 bg-[#1c1b1f] text-[#ffd64e] font-black text-xs rounded-full shadow hover:bg-black transition cursor-pointer flex items-center gap-2 self-start sm:self-auto"
            >
              <Users className="w-4 h-4" />
              <span>View Full Roster ({students.length} Students)</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {DISABILITY_CLUSTERS.map((cluster) => {
              const clusterStudents = students.filter(st => {
                const c = getDisabilityForStudent(st.disabilityCategory);
                return c.id === cluster.id;
              });

              return (
                <div 
                  key={cluster.id}
                  className="bg-white rounded-[28px] border border-black/10 p-6 shadow-sm hover:shadow-xl hover:border-black/20 transition-all duration-300 flex flex-col justify-between space-y-4 group"
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="w-12 h-12 rounded-2xl bg-[#f9f6f0] border border-black/10 flex items-center justify-center text-xl font-black shadow-xs group-hover:scale-110 transition-transform">
                        <cluster.icon className="w-6 h-6 text-[#1c1b1f]" />
                      </div>
                      <span className="px-2.5 py-0.5 rounded-full bg-[#ffd64e] text-black text-[10px] font-mono font-black border border-black/10">
                        {clusterStudents.length} Students
                      </span>
                    </div>

                    <div>
                      <div className="text-[10px] font-mono font-bold uppercase text-slate-400">
                        DSM-5: {cluster.dsmCode}
                      </div>
                      <h3 className="text-base font-black text-[#1c1b1f] group-hover:text-amber-700 transition-colors">
                        {cluster.shortName}
                      </h3>
                      <div className="text-[11px] font-semibold text-slate-500">
                        {cluster.departmentName}
                      </div>
                    </div>

                    <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                      {cluster.description}
                    </p>

                    {/* Standardized Clinical Battery */}
                    <div className="p-2.5 rounded-xl bg-[#f9f6f0] border border-black/5 text-[10px] space-y-1">
                      <div className="font-bold text-[#1c1b1f] uppercase tracking-wider">Assessment Battery:</div>
                      <div className="text-slate-600 truncate font-mono">
                        {cluster.targetAssessments.slice(0, 2).join(' • ')}
                      </div>
                    </div>

                    {/* Enrolled Student Avatars */}
                    <div className="space-y-1 pt-1">
                      <div className="text-[10px] uppercase font-bold text-slate-400">Enrolled Students:</div>
                      {clusterStudents.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {clusterStudents.map(st => (
                            <span 
                              key={st.id}
                              className="px-2 py-0.5 rounded-full bg-slate-100 text-[#1c1b1f] text-[10px] font-bold border border-slate-200"
                              title={`${st.name} (${st.rollId})`}
                            >
                              {st.name.split(' ')[0]}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="text-[10px] text-slate-400 italic">No students currently enrolled</div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-2 border-t border-black/5 space-y-2">
                    <button
                      onClick={() => {
                        setSelectedWingFilter(cluster.id);
                        setDashboardTab('roster');
                      }}
                      className="w-full py-2.5 rounded-full bg-[#1c1b1f] hover:bg-black text-[#ffd64e] font-black text-xs flex items-center justify-center gap-1.5 transition cursor-pointer"
                    >
                      <Users className="w-3.5 h-3.5" />
                      <span>View Wing Roster ({clusterStudents.length})</span>
                    </button>

                    {onNavigateToDisabilitySuite && (
                      <button
                        onClick={() => onNavigateToDisabilitySuite(cluster.id)}
                        className="w-full py-2 rounded-full bg-[#f9f6f0] hover:bg-slate-200 text-slate-800 font-bold text-xs flex items-center justify-center gap-1.5 transition cursor-pointer border border-black/10"
                      >
                        <Stethoscope className="w-3.5 h-3.5 text-slate-700" />
                        <span>Clinical Specialty Suite ↗</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* VIEW 1: PROMINENT 30-MODULE SUITE LAUNCHER */}
      {dashboardTab === 'modules' && (
        <div className="space-y-4">
          <ModuleExplorer currentUser={currentUser} />
        </div>
      )}

      {/* VIEW 2: 30-MODULE VIDEO & CLINICAL TUTORIAL HUB */}
      {dashboardTab === 'tutorials' && (
        <ModuleTutorialHub />
      )}

      {/* VIEW 3: STUDENT ROSTER TABLE WITH LOGIN CREDENTIALS & 30-MODULE CLINICAL ANALYSIS TRIGGER */}
      {dashboardTab === 'roster' && (
        <div className="bg-[#f9f6f0] rounded-[32px] border border-black/10 shadow-sm p-6 sm:p-8 space-y-6">
          
          {/* Department Filter Bar */}
          <div className="space-y-2">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 font-bold">
              Filter by Clinical Disability Wing:
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setSelectedWingFilter('all')}
                className={`px-3.5 py-1.5 rounded-full text-xs font-black transition cursor-pointer ${
                  selectedWingFilter === 'all'
                    ? 'bg-[#1c1b1f] text-[#ffd64e] shadow'
                    : 'bg-white text-slate-700 hover:bg-slate-100 border border-black/10'
                }`}
              >
                All Wings ({students.length})
              </button>

              {DISABILITY_CLUSTERS.map((cluster) => {
                const count = students.filter(s => getDisabilityForStudent(s.disabilityCategory).id === cluster.id).length;
                const isSelected = selectedWingFilter === cluster.id;

                return (
                  <button
                    key={cluster.id}
                    onClick={() => setSelectedWingFilter(cluster.id)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-black transition flex items-center gap-1.5 cursor-pointer ${
                      isSelected
                        ? 'bg-[#1c1b1f] text-[#ffd64e] shadow'
                        : 'bg-white text-slate-700 hover:bg-slate-100 border border-black/10'
                    }`}
                  >
                    <span>{cluster.shortName}</span>
                    <span className="font-mono text-[10px] opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active Department Dossier Bar if filtered */}
          {selectedWingFilter !== 'all' && (() => {
            const activeCluster = DISABILITY_CLUSTERS.find(c => c.id === selectedWingFilter);
            if (!activeCluster) return null;

            return (
              <div className="p-4 bg-white rounded-2xl border border-black/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs shadow-xs">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#f9f6f0] border border-black/10 flex items-center justify-center font-black">
                    <activeCluster.icon className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <div className="font-black text-[#1c1b1f] text-sm flex items-center gap-2">
                      <span>{activeCluster.name}</span>
                      <span className="px-2 py-0.5 bg-[#ffd64e]/30 text-black rounded-full text-[10px] font-bold">
                        RPwD 2016 Act
                      </span>
                    </div>
                    <div className="text-slate-600 text-[11px]">
                      DSM-5: <strong className="font-mono">{activeCluster.dsmCode}</strong> • Battery: {activeCluster.targetAssessments.join(', ')}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedWingFilter('all')}
                  className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-full text-[11px] transition cursor-pointer"
                >
                  Clear Wing Filter ✕
                </button>
              </div>
            );
          })()}

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-[#1c1b1f]/40 absolute left-3.5 top-3" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search student, email, Roll ID, category..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-black/10 rounded-full text-xs font-bold text-[#1c1b1f] placeholder:text-[#1c1b1f]/40 focus:ring-2 focus:ring-[#ffd64e] focus:outline-none"
              />
            </div>

            <div className="text-xs text-[#1c1b1f]/70 font-bold bg-white px-4 py-2 rounded-full border border-black/5 shadow-sm">
              Showing {filteredStudents.length} of {students.length} students
            </div>
          </div>

          <div className="overflow-x-auto border border-black/10 rounded-[24px] bg-white shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#1c1b1f] text-white/90 text-[11px] font-black uppercase tracking-wider">
                  <th className="p-3.5">Roll ID</th>
                  <th className="p-3.5">Student Name</th>
                  <th className="p-3.5">Student Portal Login Credentials</th>
                  <th className="p-3.5">Disability Category</th>
                  <th className="p-3.5">Mastery Index</th>
                  <th className="p-3.5">Clinical Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5 text-xs">
                {filteredStudents.map((st) => (
                  <tr key={st.id} className="hover:bg-[#eae7e1]/30 transition">
                    <td className="p-3.5 font-mono font-black text-[#1c1b1f]">{st.rollId}</td>
                    <td className="p-3.5">
                      <div className="font-extrabold text-[#1c1b1f] text-sm">{st.name}</div>
                      <div className="text-[10px] text-[#1c1b1f]/60 font-medium">{st.grade} • Guardian: {st.guardianName}</div>
                    </td>

                    {/* Student Login Credentials Column */}
                    <td className="p-3.5 space-y-1.5">
                      <div className="flex items-center gap-1.5">
                        <Key className="w-3.5 h-3.5 text-[#d97706]" />
                        <span className="font-mono text-[#1c1b1f] font-extrabold text-[11px]">{st.studentEmail}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-[#1c1b1f]/70">
                        <span>Pwd: <strong className="font-mono text-[#1c1b1f]">{st.studentPassword || 'Student#2026'}</strong></span>
                        <span>PIN: <strong className="font-mono text-emerald-700 font-bold">{st.portalPin || '1234'}</strong></span>
                        <button
                          onClick={() => handleCopyCredentials(st)}
                          className="px-2 py-0.5 bg-[#eae7e1] hover:bg-[#1c1b1f] hover:text-white text-[#1c1b1f] rounded-full font-bold transition flex items-center gap-1 text-[9px]"
                        >
                          {copiedId === st.id ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 text-current" />}
                          <span>{copiedId === st.id ? 'Copied!' : 'Copy'}</span>
                        </button>
                      </div>
                    </td>

                    <td className="p-3.5">
                      <div className="flex flex-col gap-1 items-start">
                        <span className="px-3 py-1 bg-[#ffd64e]/20 text-[#1c1b1f] border border-[#ffd64e]/40 rounded-full font-bold text-[10px]">
                          {st.disabilityCategory}
                        </span>
                        {st.coOccurringDisabilityCategory && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-800 border border-purple-300 rounded-full font-bold text-[9px] flex items-center gap-1">
                            <span>+ Dual:</span>
                            <span>{st.coOccurringDisabilityCategory}</span>
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3.5 font-mono font-black text-emerald-700 text-sm">
                      {st.overallMasteryPercent}%
                    </td>
                    <td className="p-3.5">
                      <button
                        onClick={() => handleOpenAnalysis(st)}
                        className="px-4 py-2 bg-[#1c1b1f] hover:bg-black text-[#ffd64e] border border-[#ffd64e]/30 font-black text-[11px] rounded-full shadow-sm transition flex items-center gap-1.5 group"
                      >
                        <BarChart2 className="w-3.5 h-3.5 text-[#ffd64e] group-hover:scale-110 transition-transform" />
                        <span>30-Module Analysis</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* VIEW 4: ENROL SINGLE STUDENT WITH INCLUSIVE LOGIN CREDENTIALS */}
      {dashboardTab === 'add_single' && (
        <div className="bg-[#f9f6f0] rounded-[32px] border border-black/10 shadow-sm p-6 sm:p-8 max-w-2xl mx-auto space-y-6">
          <h3 className="text-xl font-black text-[#1c1b1f] border-b border-black/10 pb-3">Enrol Single Student with Login Credentials</h3>
          
          <form onSubmit={handleAddSingleStudent} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-[#1c1b1f] mb-1">Student Full Name *</label>
                <input
                  type="text" required
                  value={singleName} onChange={(e) => setSingleName(e.target.value)}
                  placeholder="Master Aarav Sharma"
                  className="w-full px-3.5 py-2.5 bg-white border border-black/15 rounded-xl text-xs font-bold text-[#1c1b1f] focus:ring-2 focus:ring-[#ffd64e] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#1c1b1f] mb-1">Roll / Student ID</label>
                <input
                  type="text"
                  value={singleRollId} onChange={(e) => setSingleRollId(e.target.value)}
                  placeholder="STU-2026-099"
                  className="w-full px-3.5 py-2.5 bg-white border border-black/15 rounded-xl text-xs font-bold text-[#1c1b1f] focus:ring-2 focus:ring-[#ffd64e] focus:outline-none"
                />
              </div>
            </div>

            {/* Inclusive Student Login Credentials Fields */}
            <div className="bg-white border border-black/10 p-5 rounded-[24px] space-y-3 shadow-sm">
              <div className="text-xs font-black text-[#1c1b1f] flex items-center gap-1.5">
                <Key className="w-4 h-4 text-[#d97706]" />
                <span>Student Mobile App & Web Login Credentials</span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-[11px] font-bold text-[#1c1b1f]/70 mb-1">Student Portal Email *</label>
                  <input
                    type="email"
                    value={singleStudentEmail} onChange={(e) => setSingleStudentEmail(e.target.value)}
                    placeholder="student.name@bugfreeschool.org"
                    className="w-full px-3 py-2 bg-[#f9f6f0] border border-black/10 rounded-xl text-xs font-bold focus:ring-2 focus:ring-[#ffd64e]"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-[#1c1b1f]/70 mb-1">Portal PIN</label>
                  <input
                    type="text"
                    value={singlePortalPin} onChange={(e) => setSinglePortalPin(e.target.value)}
                    placeholder="1234"
                    className="w-full px-3 py-2 bg-[#f9f6f0] border border-black/10 rounded-xl text-xs font-mono font-bold focus:ring-2 focus:ring-[#ffd64e]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-[#1c1b1f]/70 mb-1">Student Portal Password</label>
                <input
                  type="text"
                  value={singleStudentPassword} onChange={(e) => setSingleStudentPassword(e.target.value)}
                  placeholder="Student#2026Password"
                  className="w-full px-3 py-2 bg-[#f9f6f0] border border-black/10 rounded-xl text-xs font-mono font-bold focus:ring-2 focus:ring-[#ffd64e]"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-[#1c1b1f] mb-1">Grade / Classroom</label>
                <input
                  type="text"
                  value={singleGrade} onChange={(e) => setSingleGrade(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white border border-black/15 rounded-xl text-xs font-bold text-[#1c1b1f] focus:ring-2 focus:ring-[#ffd64e]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#1c1b1f] mb-1">Disability Category</label>
                <select
                  value={singleCategory} onChange={(e) => setSingleCategory(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white border border-black/15 rounded-xl text-xs font-bold text-[#1c1b1f] focus:ring-2 focus:ring-[#ffd64e]"
                >
                  {DISABILITY_CLUSTERS.map(c => (
                    <option key={c.id} value={c.name}>{c.shortName} — {c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-[#1c1b1f] mb-1">Guardian Name *</label>
                <input
                  type="text" required
                  value={singleGuardianName} onChange={(e) => setSingleGuardianName(e.target.value)}
                  placeholder="Parent / Guardian Name"
                  className="w-full px-3.5 py-2.5 bg-white border border-black/15 rounded-xl text-xs font-bold text-[#1c1b1f] focus:ring-2 focus:ring-[#ffd64e]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#1c1b1f] mb-1">Guardian Phone</label>
                <input
                  type="text"
                  value={singleGuardianPhone} onChange={(e) => setSingleGuardianPhone(e.target.value)}
                  placeholder="+91 98000 00000"
                  className="w-full px-3.5 py-2.5 bg-white border border-black/15 rounded-xl text-xs font-bold text-[#1c1b1f] focus:ring-2 focus:ring-[#ffd64e]"
                />
              </div>
            </div>

            <div className="pt-3 border-t border-black/10 flex justify-end gap-3">
              <button type="button" onClick={() => setDashboardTab('roster')} className="px-5 py-2.5 bg-[#eae7e1] text-[#1c1b1f] font-black rounded-full text-xs hover:bg-[#e0ddd7] transition">Cancel</button>
              <button type="submit" className="px-6 py-2.5 bg-[#ffd64e] hover:bg-[#ffcf33] text-[#1c1b1f] font-black rounded-full text-xs shadow-md transition">Enrol Student</button>
            </div>
          </form>
        </div>
      )}

      {/* VIEW 5: BULK UPLOAD EXCEL INCLUSIVE OF STUDENT LOGIN CREDENTIALS */}
      {dashboardTab === 'bulk_upload' && (
        <div className="bg-[#f9f6f0] rounded-[32px] border border-black/10 shadow-sm p-6 sm:p-8 max-w-3xl mx-auto space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-black/10 pb-4 gap-3">
            <div>
              <h3 className="text-xl font-black text-[#1c1b1f]">Batch Bulk Student Excel / CSV Importer</h3>
              <p className="text-xs text-[#1c1b1f]/70 mt-0.5">Upload Excel spreadsheet containing student profiles AND login credentials (email, password, PIN)</p>
            </div>

            <a
              href="/downloads/abilityos_student_import_template.csv"
              download
              className="px-4 py-2 bg-white hover:bg-[#eae7e1] text-[#1c1b1f] font-black text-xs rounded-full border border-black/10 flex items-center gap-1.5 transition shadow-sm shrink-0"
            >
              <Download className="w-4 h-4 text-[#1c1b1f]" />
              <span>Download Template</span>
            </a>
          </div>

          <div className="border-2 border-dashed border-black/20 rounded-[28px] p-8 text-center space-y-3 bg-white hover:bg-white/80 transition">
            <Upload className="w-10 h-10 text-[#1c1b1f] mx-auto" />
            <div className="text-sm font-black text-[#1c1b1f]">
              Drag & Drop your student Excel / CSV file here
            </div>

            <input
              type="file"
              accept=".csv, .xlsx, .xls"
              onChange={handleFileChange}
              className="hidden"
              id="excel-file-input"
            />
            <label
              htmlFor="excel-file-input"
              className="inline-block px-6 py-2.5 bg-[#ffd64e] hover:bg-[#ffcf33] text-[#1c1b1f] font-black text-xs rounded-full shadow cursor-pointer transition"
            >
              Select Excel File
            </label>
          </div>

          {/* Parsed Preview Table showing inclusive login credentials */}
          {parsedPreview.length > 0 && (
            <div className="space-y-3 border-t border-black/10 pt-4">
              <h4 className="font-black text-xs text-[#1c1b1f] flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                Parsed Student Roster Preview (Inclusive of Login Credentials):
              </h4>

              <div className="overflow-x-auto border border-black/10 rounded-[20px] bg-white">
                <table className="w-full text-left text-xs">
                  <thead className="bg-[#1c1b1f] text-white/90 text-[10px] font-black uppercase tracking-wider">
                    <tr>
                      <th className="p-3">Name</th>
                      <th className="p-3">Roll ID</th>
                      <th className="p-3">Student Email</th>
                      <th className="p-3">Password</th>
                      <th className="p-3">PIN</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/5">
                    {parsedPreview.map((item, i) => (
                      <tr key={i} className="font-medium hover:bg-[#eae7e1]/20 transition">
                        <td className="p-3 font-bold text-[#1c1b1f]">{item.name}</td>
                        <td className="p-3 font-mono text-[#1c1b1f]/70">{item.rollId}</td>
                        <td className="p-3 font-mono text-[#1c1b1f] font-bold">{item.email}</td>
                        <td className="p-3 font-mono text-[#1c1b1f]/80">{item.pwd}</td>
                        <td className="p-3 font-mono text-emerald-700 font-bold">{item.pin}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                onClick={handleConfirmBulkUpload}
                className="w-full py-3 bg-[#ffd64e] hover:bg-[#ffcf33] text-[#1c1b1f] font-black text-xs rounded-full shadow-md transition"
              >
                Confirm & Import {parsedPreview.length} Students with Login Credentials
              </button>
            </div>
          )}

        </div>
      )}

      {/* Individual Student 30-Module Clinical Analysis Modal */}
      <StudentClinicalAnalysisModal 
        student={selectedStudentForModal}
        isOpen={isAnalysisModalOpen}
        onClose={() => setIsAnalysisModalOpen(false)}
        onSelectModule={() => setDashboardTab('modules')}
      />

    </div>
  );
};
